/**
 * Week and date range utilities — CLIENT side.
 *
 * Companion to server/utils/dateRange.js. The two must agree on:
 *   - Where week boundaries fall (uses company timezone + start_of_week)
 *   - YYYY-MM-DD date strings represent LOCAL dates in the company timezone
 *
 * IMPORTANT: This module replaces several inline week-start computations
 * scattered across the client (Reports.js, Timesheets.js, PaystubExportModal.js).
 * Use the helpers here exclusively. Do not call `today.getDay()` or
 * `today.getDate()` directly for week math — those run in the browser's
 * timezone, not the company timezone, and have caused real bugs.
 */

import { formatInTimeZone } from 'date-fns-tz';

/**
 * Render a Date's UTC calendar fields, regardless of the browser's timezone.
 *
 * Every Date built in this module is `Date.UTC(y, m - 1, d, 12, 0, 0)` — noon
 * UTC — precisely so day arithmetic cannot be flipped by a DST transition, as
 * the doc comments below say. `date-fns`'s bare `format` then read those UTC
 * fields back through the BROWSER's offset, which throws that anchoring away:
 * the noon anchor buys ±12 hours of headroom and the local rendering spends it.
 *
 * Latent rather than live for this company — Eastern is UTC-4/-5, so noon UTC
 * is the same calendar day and every value here is correct today. It goes past
 * the anchor only above UTC+12 (Kiritimati, Apia, Tongatapu are +13/+14), where
 * `startOfWeekDateString` would return tomorrow's week start.
 *
 * Fixed anyway, for the reason in this file's own header: it must agree with
 * server/utils/dateRange.js, where the identical `format` call was NOT latent —
 * that one rendered real instants rather than noon anchors, so it was wrong at
 * every non-zero offset. Leaving the client half "correct for the offsets our
 * crews happen to use" is a property that holds by accident.
 */
const formatUtc = (date, pattern) => formatInTimeZone(date, 'UTC', pattern);

export const DEFAULT_TIMEZONE = 'America/New_York';
export const DEFAULT_START_OF_WEEK = 1; // Monday

/**
 * Read a settings object (as returned from /api/settings) and pull out the
 * timezone + start-of-week, with defaults.
 *
 * @param {object} settings - { default_timezone, start_of_week, ... }
 * @returns {{ timezone: string, startOfWeek: number }}
 */
export function getCompanyDateConfig(settings) {
  const timezone = (settings && settings.default_timezone) || DEFAULT_TIMEZONE;
  let startOfWeek = DEFAULT_START_OF_WEEK;
  if (settings && settings.start_of_week !== undefined && settings.start_of_week !== null && settings.start_of_week !== '') {
    const parsed = parseInt(settings.start_of_week, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 6) {
      startOfWeek = parsed;
    }
  }
  return { timezone, startOfWeek };
}

/**
 * Convert a Date instance to its YYYY-MM-DD string in the given timezone.
 *
 * @param {Date} date
 * @param {string} timezone
 * @returns {string}
 */
export function toLocalDateString(date, timezone) {
  return formatInTimeZone(date, timezone, 'yyyy-MM-dd');
}

/**
 * Today's date string (YYYY-MM-DD) in the company timezone.
 *
 * @param {string} timezone
 * @returns {string}
 */
export function todayInTz(timezone) {
  return toLocalDateString(new Date(), timezone);
}

/**
 * Compute the start-of-week date string for a given local reference date.
 * Uses noon-UTC anchoring to avoid DST flip-flopping.
 *
 * @param {string} referenceLocalDate - YYYY-MM-DD
 * @param {number} startOfWeek - 0..6 (0=Sunday)
 * @returns {string} YYYY-MM-DD
 */
export function startOfWeekDateString(referenceLocalDate, startOfWeek) {
  const [y, m, d] = referenceLocalDate.split('-').map(n => parseInt(n, 10));
  const ref = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const dayOfWeek = ref.getUTCDay();
  const diff = (dayOfWeek - startOfWeek + 7) % 7;
  ref.setUTCDate(ref.getUTCDate() - diff);
  return formatUtc(ref, 'yyyy-MM-dd');
}

/**
 * Compute the end-of-week date string (start + 6 days).
 */
export function endOfWeekDateString(startOfWeekLocalDate) {
  const [y, m, d] = startOfWeekLocalDate.split('-').map(n => parseInt(n, 10));
  const ref = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  ref.setUTCDate(ref.getUTCDate() + 6);
  return formatUtc(ref, 'yyyy-MM-dd');
}

/**
 * Generate week options (for week pickers like in Timesheets and PaystubExport).
 * Returns an array of { startDate, endDate, label, isCurrent } objects covering
 * `weeksBack` past weeks through `weeksForward` future weeks.
 *
 * @param {object} settings - settings object from /api/settings
 * @param {number} weeksBack - how many weeks before current to include (default 12)
 * @param {number} weeksForward - how many weeks after current to include (default 2)
 * @returns {Array<{ startDate: string, endDate: string, label: string, isCurrent: boolean }>}
 */
export function generateWeekOptions(settings, weeksBack = 12, weeksForward = 2) {
  const { timezone, startOfWeek } = getCompanyDateConfig(settings);
  const today = todayInTz(timezone);
  const currentStart = startOfWeekDateString(today, startOfWeek);

  const out = [];
  for (let i = -weeksBack; i <= weeksForward; i++) {
    const [y, m, d] = currentStart.split('-').map(n => parseInt(n, 10));
    const wsRef = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    wsRef.setUTCDate(wsRef.getUTCDate() + i * 7);
    const startDate = formatUtc(wsRef, 'yyyy-MM-dd');
    const endDate = endOfWeekDateString(startDate);

    const label = `${formatLocalDateShort(startDate)} — ${formatLocalDateShort(endDate, true)}`;

    out.push({ startDate, endDate, label, isCurrent: i === 0 });
  }
  return out;
}

/**
 * Get the current week's local date range.
 */
export function getCurrentWeekRange(settings) {
  const { timezone, startOfWeek } = getCompanyDateConfig(settings);
  const today = todayInTz(timezone);
  const startDate = startOfWeekDateString(today, startOfWeek);
  const endDate = endOfWeekDateString(startDate);
  return { startDate, endDate, timezone, startOfWeek };
}

/**
 * Build week ranges between two local dates (inclusive), aligned to the
 * configured start_of_week. Used by paystub export to break a date range
 * into weekly chunks.
 *
 * @param {string} startLocalDate - YYYY-MM-DD inclusive
 * @param {string} endLocalDate - YYYY-MM-DD inclusive
 * @param {object} settings
 * @returns {Array<{ start: string, end: string }>}
 */
export function getWeekRangesBetween(startLocalDate, endLocalDate, settings) {
  const { startOfWeek } = getCompanyDateConfig(settings);
  const ranges = [];
  let cursor = startOfWeekDateString(startLocalDate, startOfWeek);
  while (cursor <= endLocalDate) {
    const wEnd = endOfWeekDateString(cursor);
    // Clip the range to the requested window
    const start = cursor < startLocalDate ? startLocalDate : cursor;
    const end = wEnd > endLocalDate ? endLocalDate : wEnd;
    ranges.push({ start, end });
    // Advance to next week
    const [y, m, d] = cursor.split('-').map(n => parseInt(n, 10));
    const next = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    next.setUTCDate(next.getUTCDate() + 7);
    cursor = formatUtc(next, 'yyyy-MM-dd');
  }
  return ranges;
}

/**
 * Format a YYYY-MM-DD local date string as "Mon DD" or "Mon DD, YYYY".
 * Avoids the local-timezone parsing pitfall by anchoring to noon UTC.
 *
 * @param {string} localDateStr - YYYY-MM-DD
 * @param {boolean} withYear - include year (default false)
 * @returns {string}
 */
export function formatLocalDateShort(localDateStr, withYear = false) {
  if (!localDateStr) return '';
  const [y, m, d] = localDateStr.split('-').map(n => parseInt(n, 10));
  const ref = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const opts = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  if (withYear) opts.year = 'numeric';
  return new Intl.DateTimeFormat('en-US', opts).format(ref);
}

/**
 * Day-name label for the start_of_week setting (used in Settings UI).
 */
export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
